(() => {
  const root = document.documentElement;
  const wishlistStorageKey = 'ringidea:wishlist:v1';
  let wishlist = readWishlist();
  let wishlistOpener = null;

  function readWishlist() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(wishlistStorageKey) || '[]');
      if (!Array.isArray(stored)) return [];
      return stored
        .filter((item) => item && item.id && item.url && item.title)
        .map((item) => ({
          id: String(item.id),
          url: String(item.url),
          title: String(item.title),
          image: String(item.image || ''),
          price: String(item.price || ''),
          comparePrice: String(item.comparePrice || ''),
        }));
    } catch {
      return [];
    }
  }

  function writeWishlist() {
    try {
      window.localStorage.setItem(wishlistStorageKey, JSON.stringify(wishlist));
    } catch {
      // Browsers can block storage in strict privacy modes. The current tab still works.
    }
  }

  function safeUrl(value, fallback = '#') {
    try {
      const parsed = new URL(value, window.location.origin);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return fallback;
      return parsed.href;
    } catch {
      return fallback;
    }
  }

  function initHeroSliders(scope = document) {
    scope.querySelectorAll('[data-ri-hero-slider]').forEach((slider) => {
      if (slider.dataset.riHeroReady === 'true') return;
      const slides = [...slider.querySelectorAll('[data-ri-hero-slide]')];
      const dots = [...slider.querySelectorAll('[data-ri-hero-dot]')];
      const previous = slider.querySelector('[data-ri-hero-previous]');
      const next = slider.querySelector('[data-ri-hero-next]');
      if (slides.length < 2) return;

      slider.dataset.riHeroReady = 'true';
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer = null;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const show = (requestedIndex, userInitiated = false) => {
        activeIndex = (requestedIndex + slides.length) % slides.length;
        slides.forEach((slide, index) => {
          const active = index === activeIndex;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          slide.querySelectorAll('a, button').forEach((control) => {
            control.tabIndex = active ? 0 : -1;
          });
        });
        dots.forEach((dot, index) => {
          const active = index === activeIndex;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
        if (userInitiated) restart();
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
      };

      const start = () => {
        if (reduceMotion || document.hidden) return;
        stop();
        timer = window.setInterval(() => show(activeIndex + 1), 6500);
      };

      const restart = () => {
        stop();
        start();
      };

      previous?.addEventListener('click', () => show(activeIndex - 1, true));
      next?.addEventListener('click', () => show(activeIndex + 1, true));
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => show(index, true));
      });
      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('focusin', stop);
      slider.addEventListener('focusout', (event) => {
        if (!slider.contains(event.relatedTarget)) start();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop();
        else start();
      });

      show(activeIndex);
      start();
    });
  }

  function setWishlistButtonState(button, saved) {
    const addLabel = button.dataset.addLabel || 'Add to wishlist';
    const removeLabel = button.dataset.removeLabel || 'Remove from wishlist';
    const label = saved ? removeLabel : addLabel;
    const icon = button.querySelector('i');
    const text = button.querySelector('[data-ri-wishlist-label]');

    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', label);
    button.title = label;
    if (text) text.textContent = label;
    if (icon) {
      icon.classList.toggle('ri-heart-3-fill', saved);
      icon.classList.toggle('ri-heart-3-line', !saved);
    }
  }

  function refreshWishlistUi() {
    const savedIds = new Set(wishlist.map((item) => item.id));
    document.querySelectorAll('[data-ri-wishlist-toggle]').forEach((button) => {
      setWishlistButtonState(button, savedIds.has(String(button.dataset.productId)));
    });

    document.querySelectorAll('[data-ri-wishlist-count]').forEach((count) => {
      count.textContent = String(wishlist.length);
      count.hidden = wishlist.length === 0;
    });

    renderWishlistDrawer();
  }

  function renderWishlistDrawer() {
    const drawer = document.querySelector('[data-ri-wishlist-drawer]');
    const container = drawer?.querySelector('[data-ri-wishlist-items]');
    const empty = drawer?.querySelector('[data-ri-wishlist-empty]');
    if (!drawer || !container || !empty) return;

    container.replaceChildren();
    empty.hidden = wishlist.length > 0;
    const viewLabel = drawer.dataset.viewLabel || 'View product';
    const removeLabel = drawer.dataset.removeLabel || 'Remove';

    wishlist.forEach((item) => {
      const row = document.createElement('article');
      row.className = 'ri-wishlist-item';

      const media = document.createElement('a');
      media.className = 'ri-wishlist-item__media';
      media.href = safeUrl(item.url);
      if (item.image) {
        const image = document.createElement('img');
        image.src = safeUrl(item.image, '');
        image.alt = '';
        image.loading = 'lazy';
        media.append(image);
      }

      const copy = document.createElement('div');
      copy.className = 'ri-wishlist-item__copy';

      const title = document.createElement('h3');
      const titleLink = document.createElement('a');
      titleLink.href = safeUrl(item.url);
      titleLink.textContent = item.title;
      title.append(titleLink);

      const prices = document.createElement('div');
      prices.className = 'ri-wishlist-item__prices';
      if (item.comparePrice) {
        const compare = document.createElement('s');
        compare.textContent = item.comparePrice;
        prices.append(compare);
      }
      const price = document.createElement('strong');
      price.textContent = item.price;
      prices.append(price);

      const actions = document.createElement('div');
      actions.className = 'ri-wishlist-item__actions';
      const view = document.createElement('a');
      view.href = safeUrl(item.url);
      view.textContent = viewLabel;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.riWishlistRemove = item.id;
      remove.textContent = removeLabel;
      actions.append(view, remove);

      copy.append(title, prices, actions);
      row.append(media, copy);
      container.append(row);
    });
  }

  function toggleWishlist(button) {
    const id = String(button.dataset.productId || '');
    if (!id) return;
    const existingIndex = wishlist.findIndex((item) => item.id === id);

    if (existingIndex >= 0) {
      wishlist.splice(existingIndex, 1);
    } else {
      wishlist.unshift({
        id,
        url: button.dataset.productUrl || '#',
        title: button.dataset.productTitle || '',
        image: button.dataset.productImage || '',
        price: button.dataset.productPrice || '',
        comparePrice: button.dataset.productCompare || '',
      });
    }

    writeWishlist();
    refreshWishlistUi();
  }

  function openWishlist(opener) {
    const drawer = document.querySelector('[data-ri-wishlist-drawer]');
    const backdrop = document.querySelector('.ri-wishlist-backdrop');
    if (!drawer || !backdrop) return;
    wishlistOpener = opener || document.activeElement;
    drawer.hidden = false;
    backdrop.hidden = false;
    root.classList.add('ri-wishlist-open');
    document.querySelectorAll('[data-ri-wishlist-open]').forEach((button) => {
      button.setAttribute('aria-expanded', 'true');
    });
    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      backdrop.classList.add('is-open');
      drawer.querySelector('[data-ri-wishlist-close]')?.focus();
    });
  }

  function closeWishlist() {
    const drawer = document.querySelector('[data-ri-wishlist-drawer]');
    const backdrop = document.querySelector('.ri-wishlist-backdrop');
    if (!drawer || !backdrop || drawer.hidden) return;
    drawer.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    root.classList.remove('ri-wishlist-open');
    document.querySelectorAll('[data-ri-wishlist-open]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
    window.setTimeout(() => {
      drawer.hidden = true;
      backdrop.hidden = true;
    }, 180);
    wishlistOpener?.focus?.();
  }

  document.addEventListener('click', (event) => {
    const wishlistOpen = event.target.closest('[data-ri-wishlist-open]');
    if (wishlistOpen) {
      openWishlist(wishlistOpen);
      return;
    }

    const wishlistClose = event.target.closest('[data-ri-wishlist-close]');
    if (wishlistClose) {
      closeWishlist();
      return;
    }

    const wishlistToggle = event.target.closest('[data-ri-wishlist-toggle]');
    if (wishlistToggle) {
      event.preventDefault();
      toggleWishlist(wishlistToggle);
      return;
    }

    const wishlistRemove = event.target.closest('[data-ri-wishlist-remove]');
    if (wishlistRemove) {
      wishlist = wishlist.filter((item) => item.id !== String(wishlistRemove.dataset.riWishlistRemove));
      writeWishlist();
      refreshWishlistUi();
      return;
    }

    const menuButton = event.target.closest('[data-ri-menu-toggle]');
    if (menuButton) {
      const menu = document.querySelector('[data-ri-mobile-menu]');
      if (!menu) return;
      const open = menu.hasAttribute('hidden');
      menu.toggleAttribute('hidden', !open);
      menuButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      root.classList.toggle('ri-menu-open', open);
      return;
    }

    const thumbnail = event.target.closest('[data-ri-thumbnail]');
    if (thumbnail) {
      const gallery = thumbnail.closest('[data-ri-gallery]');
      const mainImage = gallery?.querySelector('.ri-product-main-media img');
      if (!mainImage) return;
      mainImage.src = thumbnail.dataset.imageUrl;
      mainImage.alt = thumbnail.dataset.imageAlt || '';
      gallery.querySelectorAll('[data-ri-thumbnail]').forEach((item) => {
        item.setAttribute('aria-current', item === thumbnail ? 'true' : 'false');
      });
      return;
    }

    const carouselButton = event.target.closest('[data-ri-carousel-scroll]');
    if (carouselButton) {
      const carousel = carouselButton.closest('[data-ri-product-carousel]');
      const track = carousel?.querySelector('[data-ri-carousel-track]');
      const card = track?.querySelector('.ri-product-card');
      if (!track || !card) return;
      const direction = Number.parseInt(carouselButton.dataset.riCarouselScroll || '1', 10);
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');
      const step = card.getBoundingClientRect().width + gap;
      const visibleCards = Math.max(1, Math.floor((track.clientWidth + gap) / step));
      track.scrollBy({
        left: direction * step * Math.max(1, visibleCards - 1),
        behavior: 'smooth',
      });
      return;
    }

    const quantityButton = event.target.closest('[data-ri-quantity-change]');
    if (quantityButton) {
      const wrapper = quantityButton.closest('[data-ri-quantity]');
      const input = wrapper?.querySelector('input[name="quantity"]');
      if (!input) return;
      const delta = Number.parseInt(quantityButton.dataset.riQuantityChange, 10);
      const minimum = Number.parseInt(input.min || '1', 10);
      const maximum = Number.parseInt(input.max || '999', 10);
      const nextValue = Math.min(maximum, Math.max(minimum, Number.parseInt(input.value || '1', 10) + delta));
      input.value = String(nextValue);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeWishlist();
  });

  document.addEventListener('change', (event) => {
    const variantControl = event.target.closest('[data-ri-variant-select], [data-ri-variant-option]');
    if (!variantControl) return;
    const selectedOption = variantControl.matches('select')
      ? variantControl.options[variantControl.selectedIndex]
      : variantControl;
    const productRoot = variantControl.closest('[data-ri-product]');
    const price = productRoot?.querySelector('[data-ri-product-price]');
    const comparePrice = productRoot?.querySelector('[data-ri-product-compare]');
    const discount = productRoot?.querySelector('[data-ri-product-discount]');
    const productCode = productRoot?.querySelector('[data-ri-product-code]');
    const submit = productRoot?.querySelector('[data-ri-add-to-cart]');
    const wishlistToggle = productRoot?.querySelector('[data-ri-wishlist-toggle]');
    if (price && selectedOption.dataset.price) price.textContent = selectedOption.dataset.price;
    if (comparePrice) {
      const hasDiscount = Boolean(selectedOption.dataset.comparePrice);
      comparePrice.hidden = !hasDiscount;
      comparePrice.textContent = selectedOption.dataset.comparePrice || '';
      if (discount) {
        discount.hidden = !hasDiscount;
        discount.textContent = hasDiscount ? `−${selectedOption.dataset.discountPercent}%` : '';
      }
    }
    if (wishlistToggle) {
      wishlistToggle.dataset.productPrice = selectedOption.dataset.price || '';
      wishlistToggle.dataset.productCompare = selectedOption.dataset.comparePrice || '';
    }
    if (productCode) productCode.textContent = selectedOption.dataset.sku || '';
    if (selectedOption.dataset.imageUrl) {
      const gallery = productRoot?.querySelector('[data-ri-gallery]');
      const matchingThumbnail = selectedOption.dataset.mediaId
        ? gallery?.querySelector(`[data-ri-thumbnail][data-media-id="${CSS.escape(selectedOption.dataset.mediaId)}"]`)
        : null;
      if (matchingThumbnail) {
        matchingThumbnail.click();
      } else {
        const mainImage = gallery?.querySelector('.ri-product-main-media img');
        if (mainImage) {
          mainImage.src = selectedOption.dataset.imageUrl;
          mainImage.alt = selectedOption.dataset.imageAlt || '';
        }
      }
    }
    if (submit) {
      const available = selectedOption.dataset.available === 'true';
      submit.disabled = !available;
      submit.querySelector('span').textContent = available
        ? submit.dataset.addLabel
        : submit.dataset.soldOutLabel;
    }
  });

  initHeroSliders();
  document.addEventListener('shopify:section:load', (event) => initHeroSliders(event.target));
  refreshWishlistUi();
})();
